<template>
	<section
		class="view-play-trainer"
		v-bind:class="{
			'state-ready': isReady,
			'state-show-english': true,
			'state-show-power': true,
		}"
	>
		<ChalkyTrainerSceneFreeplay
			v-bind:autoActivate="true"
			v-bind:autoStartAmbient="true"
			v-bind:autoStartMusic="true"
			v-bind:debugMode="false"
			v-bind:diagramId="diagramId"
			v-bind:diagramInverse="true"
			v-bind:environment="environment"
			v-bind:freelook="true"
			v-bind:separateMeters="false"
			v-bind:showControls="true"
			v-bind:showEnglishMeter="true"
			v-bind:showFootspot="false"
			v-bind:showFpsCounter="false"
			v-bind:showHeadspot="false"
			v-bind:showPowerMeter="true"
			v-bind:tableAppearance="tableAppearance"
			v-bind:tableOverlays="['overlay-breakbox']"
			v-on:game:ready="Handle_OnWorldReady"
		/>

		<!-- <ChalkyTrainerViewOverlayLowerThird /> -->

		<!-- <ChalkyTrainerViewModalMatchConfig v-model="showMatchConfig" v-on:submit="Handle_OnMatchConfigSubmit" /> -->

		<ChalkyTrainerViewModalSettings ref="modalSettings" />

		<section id="overlay">
			<section id="menu" class="padded">
				<ViewPlayControls v-on:startMenu="Handle_OnStartMenu" v-on:showSettings="Handle_OnShowSettings" />
			</section>
		</section>

		<!-- Toast Group Container -->
		<ChalkyUiToastGroup position="bottom-right" />
	</section>
</template>

<script lang="ts">
	import * as Config from '@/Config';
	import * as Event from '@chalkysticks/vue2-trainer/src/App/Event';
	import ChalkySticks from '@chalkysticks/sdk';
	import GameService from '@chalkysticks/vue2-trainer/src/App/Game';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, bind, mounted } from '@/Utility/Decorators';

	/**
	 * Async component definitions for lazy loading
	 * These will only load when the trainer is actually rendered
	 * Components are directly exported from the components.ts file
	 */
	const asyncComponents = {
		ChalkyTrainerSceneGame: () =>
			import(
				/* webpackChunkName: "trainer-controls" */
				'@chalkysticks/vue2-trainer/src/components'
			).then((m) => m.SceneGame),

		ChalkyTrainerViewEnglish: () =>
			import(
				/* webpackChunkName: "trainer-controls" */
				'@chalkysticks/vue2-trainer/src/components'
			).then((m) => m.ViewEnglish),

		ChalkyTrainerViewInputRouter: () =>
			import(
				/* webpackChunkName: "trainer-controls" */
				'@chalkysticks/vue2-trainer/src/components'
			).then((m) => m.ViewInputRouter),

		ChalkyTrainerViewPower: () =>
			import(
				/* webpackChunkName: "trainer-controls" */
				'@chalkysticks/vue2-trainer/src/components'
			).then((m) => m.ViewPower),

		// Core trainer world component
		ChalkyTrainerViewWorld: () =>
			import(
				/* webpackChunkName: "trainer-world" */
				'@chalkysticks/vue2-trainer/src/components'
			).then((m) => m.ViewWorld),
	};

	/**
	 * Self-contained 3D trainer view using SceneFreeplay. Consumes a match
	 * configuration from the global store (set by the Play modal) or
	 * falls back to default practice mode for direct navigation. Displays
	 * toast notifications for fouls and game events.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Play
	 * @project ChalkySticks App
	 */
	@Component({
		components: asyncComponents,
	})
	export default class ViewPlayTrainer extends ViewBase {
		/**
		 * Diagram ID for deep-linking, passed from the Page
		 *
		 * @type string
		 */
		@Prop({ default: null })
		public diagramId!: string | null;

		/**
		 * Default environment for the trainer
		 *
		 * @type string
		 */
		protected environment: string = Config.Plugin.TRAINER_ENVIRONMENT;

		/**
		 * Whether the 3D world has finished loading and is ready to display
		 *
		 * @type boolean
		 */
		protected isReady: boolean = false;

		/**
		 * Default table appearance for the trainer
		 *
		 * @type string
		 */
		protected tableAppearance: string = Config.Plugin.TRAINER_TABLE_APPEARANCE;

		/**
		 * Attach event listeners on mount.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('game:rack', this.Handle_OnBallsRacked);
			ChalkySticks.Core.Event.Bus.on(Event.Game.End, this.Handle_OnGameEnd);
			ChalkySticks.Core.Event.Bus.on(Event.Rules.GameComplete, this.Handle_OnToastInfo);

			// Foul notifications
			ChalkySticks.Core.Event.Bus.on(Event.Foul.Scratch, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.on(Event.Foul.NoContact, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.on(Event.Foul.NoRail, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.on(Event.Foul.OffTable, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.on(Event.Foul.ShotClock, this.Handle_OnToastError);
		}

		/**
		 * Detach event listeners before destroy.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('game:rack', this.Handle_OnBallsRacked);
			ChalkySticks.Core.Event.Bus.off(Event.Game.End, this.Handle_OnGameEnd);
			ChalkySticks.Core.Event.Bus.off(Event.Rules.GameComplete, this.Handle_OnToastInfo);

			// Foul notifications
			ChalkySticks.Core.Event.Bus.off(Event.Foul.Scratch, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.off(Event.Foul.NoContact, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.off(Event.Foul.NoRail, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.off(Event.Foul.OffTable, this.Handle_OnToastError);
			ChalkySticks.Core.Event.Bus.off(Event.Foul.ShotClock, this.Handle_OnToastError);
		}

		/**
		 * @return Promise<void>
		 */
		protected async rackBalls(): Promise<void> {
			// Load diagram
			if (this.diagramId) {
				return;
			}

			// Fallback
			ChalkySticks.Core.Event.Bus.dispatch('game.control:rack', 'EightBall');
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle balls racked event.
		 *
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnBallsRacked(): Promise<void> {
			// No-op; racking handled by the scene
		}

		/**
		 * Re-rack after game ends.
		 *
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnGameEnd(): Promise<void> {
			ChalkySticks.Core.Event.Bus.dispatch('game.control:rack');
		}

		/**
		 * TEMPORARY
		 *
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnShowSettings(e: Event): Promise<void> {
			(this.$refs.modalSettings as any).show();
		}

		/**
		 * Show an info toast for game completion or rules events.
		 *
		 * @param any e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnToastInfo(e: any): Promise<void> {
			const message = e.data?.reason || e?.data?.result?.reason;

			if (message) {
				this.$toast.info(message);
			}
		}

		/**
		 * Show an error toast for foul events.
		 *
		 * @param any e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnToastError(e: any): Promise<void> {
			const reason = e.data?.reason;

			if (reason) {
				this.$toast.error(reason);
			}
		}

		/**
		 * Handle when the 3D game world is ready.
		 * Configures and starts the match, then fades in the view.
		 *
		 * @param any gameInstance
		 * @return Promise<void>
		 */
		protected async Handle_OnWorldReady(gameInstance: any): Promise<void> {
			ChalkySticks.Utility.Log.info('trainer', 'ChalkySticks Trainer ready:', gameInstance);

			// Configure and start the match
			this.rackBalls();

			// Fade in after a short delay
			setTimeout(() => (this.isReady = true), 250);

			// Notify the page that the game is ready
			this.$emit('game:ready', gameInstance);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-play-trainer {
		--english-size: 100px;
		--padding: 2rem;

		height: 100%;
		transition: opacity 0.5s ease-in-out;
		width: 100%;

		.view-world {
			height: 100%;
			left: 0;
			position: absolute;
			top: 0;
			width: 100%;
			z-index: 1;
		}

		#overlay {
			height: 100%;
			left: 0;
			pointer-events: none;
			position: absolute;
			top: 0;
			width: 100%;
			z-index: var(--z-page-top, 1000);

			button {
				align-items: center;
				background: rgba(0, 0, 0, 0.5);
				border: 1px solid rgba(255, 255, 255, 0.2);
				border-radius: 4px;
				color: white;
				cursor: pointer;
				display: flex;
				height: 50px;
				justify-content: center;
				margin-bottom: 0.5rem;
				pointer-events: auto;
				text-align: center;
				width: 50px;

				&:hover {
					background: rgba(0, 0, 0, 0.7);
				}
			}

			#menu {
				display: flex;
				flex-direction: column;
				height: 100%;
				justify-content: flex-end;
				pointer-events: none;

				nav {
					align-items: flex-start;
					display: flex;
					flex-direction: column;
				}
			}
		}

		[class*='view-overlay-lowerthird-'] {
			bottom: 3.5rem;
			left: 50%;
			position: absolute;
			transform: translate(-50%, 0);
			z-index: 2;
		}

		.scene-game,
		.scene-freeplay {
			--english-size: 75px;

			.view-english {
				opacity: 0.85;
				transform: scale(0.75);

				&:focus,
				&:active,
				&:hover {
					transform: scale(1);
				}
			}

			.view-power {
				opacity: 0.85;
			}
		}

		.english-overlay {
			user-select: none;

			.backdrop {
				opacity: 0.33;
			}

			.ball-container {
				opacity: 0.5;
			}
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.view-play-trainer:not(.state-ready) {
		opacity: 0;
	}

	// Media Queries
	// ---------------------------------------------------------------------------

	@include media-query('<= desktop') {
		.view-play-trainer {
			.chalky.trainer-scene .view-powerenglish.is-horizontal {
				left: 80px;
			}

			.view-play-controls {
				.view-button-save,
				.view-button-cue,
				.view-button-lines,
				.view-button-rackeight,
				.view-button-racknine,
				.view-button-help {
					display: none;
				}
			}
		}
	}

	@include media-query('mobile-landscape') {
		.view-play-trainer {
			.chalky.utility-settings-panel {
				padding: 0;
			}

			.view-button-cue {
				display: initial;
			}

			[class*='view-overlay-lowerthird-'] {
				bottom: -2.25rem;
				max-width: 500px;
			}
		}
	}

	@include media-query('mobile-portrait') {
		.view-play-trainer {
			[class*='view-overlay-lowerthird-'] {
				bottom: 5rem;
				max-width: 80%;
			}
		}
	}
</style>
